import React, { useEffect, useState } from 'react';
import { governmentFormsApi } from '../../services/api';
import api from '../../services/api';
import {
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
} from 'components/icons';
import Modal from '../ui/Modal';
import { SkeletonBlock } from '../ui/Skeleton';
import toast from 'react-hot-toast';

interface FormField {
  block: string;
  label: string;
  value: string;
  source: string;
  notes?: string;
}

interface FormResult {
  formId: string;
  formTitle: string;
  fields: FormField[];
  warnings: string[];
}

const GovernmentFormsModal: React.FC<{
  opportunityId?: string;
  onClose: () => void;
}> = ({ opportunityId, onClose }) => {
  const [forms, setForms] = useState<FormResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await governmentFormsApi.getAll(opportunityId);
      setForms(res.data?.data?.forms || []);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityId]);

  const downloadDocx = async (path: string, filename: string, key: string) => {
    setDownloading(key);
    try {
      const res = await api.get(path, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`${filename} downloaded`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  const isMissing = (v: string) => typeof v === 'string' && v.startsWith('[USER:');
  const totalMissing = forms.reduce(
    (sum, f) => sum + f.fields.filter((x) => isMissing(x.value)).length,
    0,
  );
  const totalWarnings = forms.reduce((sum, f) => sum + f.warnings.length, 0);

  return (
    <Modal
      open
      onClose={onClose}
      title="Government Forms Pack"
      icon={DocumentTextIcon}
      size="xl"
      description="Pre-filled values from your company profile + opportunity context. Use these to transcribe onto the actual government PDF or paste into the agency submission portal."
      footer={
        <div className="flex w-full items-center justify-between gap-4">
          <span className="text-caption text-content-muted">
            Tip: items shown in <span className="text-danger italic">red italic</span> need values
            from your company profile (CAGE, UEI, etc.) or are signature fields.
          </span>
          <button onClick={onClose} className="btn-secondary">Close</button>
        </div>
      }
    >
      <div className="flex flex-col">

        {/* Top summary + bundle download */}
        {!loading && forms.length > 0 && (
          <div className="mb-4 px-4 py-3 bg-surface-sunk ring-1 ring-border rounded-token flex flex-wrap items-center justify-between gap-3 text-body">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-content-muted">
                <strong className="text-content">{forms.length}</strong> forms ready
              </span>
              {totalMissing > 0 && (
                <span className="text-danger">
                  <strong>{totalMissing}</strong> field{totalMissing !== 1 ? 's' : ''} need user input
                </span>
              )}
              {totalWarnings > 0 && (
                <span className="text-warning flex items-center gap-1">
                  <ExclamationTriangleIcon className="w-4 h-4" aria-hidden="true" />
                  <strong>{totalWarnings}</strong> warning{totalWarnings !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            <button
              onClick={() =>
                downloadDocx(
                  governmentFormsApi.bundleDocxUrl(opportunityId),
                  'government_forms_pack.docx',
                  'bundle',
                )
              }
              disabled={downloading === 'bundle'}
              className="btn-primary"
            >
              <ArrowDownTrayIcon className="w-4 h-4" aria-hidden="true" />
              {downloading === 'bundle' ? 'Downloading…' : 'Download All as One DOCX'}
            </button>
          </div>
        )}

        {/* Body */}
        <div className="space-y-4">
          {loading ? (
            <SkeletonBlock className="h-32" />
          ) : forms.length === 0 ? (
            <p className="text-body text-content-muted">No forms returned.</p>
          ) : (
            forms.map((form) => {
              const missingCount = form.fields.filter((f) => isMissing(f.value)).length;
              const isExpanded = expanded[form.formId];
              return (
                <div key={form.formId} className="border border-border rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-surface-sunk flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-content">{form.formTitle}</h3>
                      <div className="flex items-center gap-3 mt-1 text-xs">
                        <span className="text-content-muted">{form.fields.length} fields</span>
                        {missingCount > 0 && (
                          <span className="text-red-700 font-medium">
                            {missingCount} need user input
                          </span>
                        )}
                        {form.warnings.length > 0 && (
                          <span className="text-amber-700 font-medium flex items-center gap-1">
                            <ExclamationTriangleIcon className="w-3 h-3" />
                            {form.warnings.length} warning{form.warnings.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      {form.warnings.length > 0 && isExpanded && (
                        <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-900">
                          {form.warnings.map((w, i) => (
                            <div key={i}>• {w}</div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setExpanded({ ...expanded, [form.formId]: !isExpanded })}
                        className="text-xs text-content-muted hover:text-content px-2 py-1"
                      >
                        {isExpanded ? 'Collapse' : 'Preview fields'}
                      </button>
                      <button
                        onClick={() =>
                          downloadDocx(
                            governmentFormsApi.oneDocxUrl(form.formId, opportunityId),
                            `${form.formId}_fill_sheet.docx`,
                            form.formId,
                          )
                        }
                        disabled={downloading === form.formId}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                      >
                        <ArrowDownTrayIcon className="w-3 h-3" />
                        {downloading === form.formId ? 'Downloading…' : 'DOCX'}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-4 py-3 max-h-80 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left border-b text-content-muted">
                            <th className="py-1 pr-2 w-28">Block / Item</th>
                            <th className="py-1 pr-2 w-1/4">Label</th>
                            <th className="py-1 pr-2">Value</th>
                            <th className="py-1 w-24">Source</th>
                          </tr>
                        </thead>
                        <tbody>
                          {form.fields.map((field, i) => {
                            const missing = isMissing(field.value);
                            return (
                              <tr key={i} className="border-b last:border-0 align-top">
                                <td className="py-1.5 pr-2 font-mono text-content">{field.block}</td>
                                <td className="py-1.5 pr-2 text-content">{field.label}</td>
                                <td className={`py-1.5 pr-2 ${missing ? 'text-red-700 italic' : 'text-content'}`}>
                                  {field.value || <em className="text-content-subtle">(blank)</em>}
                                  {field.notes && (
                                    <div className="text-xs text-content-muted italic mt-0.5">
                                      Note: {field.notes}
                                    </div>
                                  )}
                                </td>
                                <td className="py-1.5 text-content-muted text-xs">
                                  {field.source.replace(/_/g, ' ')}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

      </div>
    </Modal>
  );
};

export default GovernmentFormsModal;
